import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DashLayout from '../../components/DashLayout';

const nav = [
  { to: '/dashboard',              icon: '📊', label: 'Dashboard' },
  { to: '/dashboard/chats',        icon: '💬', label: 'Live Chats' },
  { to: '/dashboard/leads',        icon: '👥', label: 'Leads' },
  { to: '/dashboard/crm',          icon: '📋', label: 'CRM' },
  { to: '/dashboard/agents',       icon: '👤', label: 'Agents' },
  { to: '/dashboard/tickets',      icon: '🎫', label: 'Support Tickets' },
  { to: '/dashboard/ai-bot',       icon: '🤖', label: 'AI Chat Bot' },
  { to: '/dashboard/api',          icon: '🔑', label: 'API Access' },
  { to: '/dashboard/branding',     icon: '🎨', label: 'Branding' },
  { to: '/dashboard/widget',       icon: '🔌', label: 'Widget & Script' },
  { to: '/dashboard/contact-form', icon: '📝', label: 'Contact Form' },
  { to: '/dashboard/analytics',    icon: '📈', label: 'Analytics' },
  { to: '/dashboard/billing',      icon: '💳', label: 'Billing' },
  { to: '/dashboard/settings',     icon: '⚙️',  label: 'Settings' },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

const whatsapp = (phone) => 'https://wa.me/' + (phone?.replace(/[^0-9]/g, '') || '');

const getSourceBadge = (lead) => {
  if (lead.lead_source === 'meta_ad')   return { label: '📘 Meta Ad',   bg: 'rgba(24,119,242,0.15)',  color: '#1877F2', border: 'rgba(24,119,242,0.3)' };
  if (lead.lead_source === 'google_ad') return { label: '🟢 Google Ad', bg: 'rgba(52,168,83,0.15)',   color: '#34A853', border: 'rgba(52,168,83,0.3)' };
  if (lead.lead_source === 'form')      return { label: '📝 Form',      bg: 'rgba(255,165,0,0.15)',   color: '#FFA500', border: 'rgba(255,165,0,0.3)' };
  return                                       { label: '💬 Chat',      bg: 'rgba(0,213,91,0.15)',    color: '#00d55b', border: 'rgba(0,213,91,0.3)' };
};

const fmt     = (dt) => dt ? new Date(dt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A';
const fmtDate = (dt) => dt ? new Date(dt).toLocaleDateString('en-IN') : 'N/A';

const parseCustomFields = (raw) => {
  if (!raw) return [];
  try { return typeof raw === 'string' ? JSON.parse(raw) : raw; }
  catch { return []; }
};

// ── Styles ────────────────────────────────────────────────────────────────────

const S = {
  panel:     { position: 'fixed', top: 0, right: 0, height: '100vh', width: 420, background: '#0d0d0d', borderLeft: '1px solid #1e1e1e', zIndex: 1000, display: 'flex', flexDirection: 'column', overflowY: 'auto' },
  overlay:   { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 999 },
  section:   { background: '#111', border: '1px solid #1e1e1e', borderRadius: 10, padding: '14px 16px', marginBottom: 14 },
  sectionHd: { fontSize: 11, fontWeight: 700, color: '#00d55b', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 12 },
  row:       { display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 },
  rowIcon:   { fontSize: 14, minWidth: 18, marginTop: 1 },
  rowLabel:  { fontSize: 11, color: '#555', marginBottom: 2 },
  rowVal:    { fontSize: 13, color: '#ccc' },
  subCard:   { background: '#0d0d0d', border: '1px solid #1e1e1e', borderRadius: 8, padding: '12px 14px', marginBottom: 10 },
  subTime:   { fontSize: 11, color: '#444', marginBottom: 8 },
  subField:  { display: 'flex', gap: 8, marginBottom: 6, alignItems: 'flex-start' },
  subLbl:    { fontSize: 11, color: '#555', minWidth: 90 },
  subVal:    { fontSize: 12, color: '#bbb', flex: 1 },
  actionBtn: { flex: 1, padding: '9px 0', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, textDecoration: 'none' },
};

// ── Detail Panel ─────────────────────────────────────────────────────────────

function LeadPanel({ lead, onClose }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    setLoading(true);
    const visitorId = lead.id || lead.visitor_id;
    if (!visitorId) { setSubmissions([]); setLoading(false); return; }
    axios.get(`/api/contact/submissions/${visitorId}`)
      .then(r => setSubmissions(r.data.data || []))
      .catch(() => setSubmissions([]))
      .finally(() => setLoading(false));
  }, [lead.id]);

  const badge = getSourceBadge(lead);

  return (
    <>
      {/* Overlay */}
      <div style={S.overlay} onClick={onClose} />

      {/* Slide-in panel */}
      <div style={S.panel}>

        {/* Header */}
        <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid #1e1e1e', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 46, height: 46, borderRadius: '50%', background: 'rgba(0,213,91,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 700, color: '#00d55b', flexShrink: 0 }}>
            {lead.name?.[0]?.toUpperCase() || '?'}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>{lead.name || 'Unknown'}</div>
            <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: badge.bg, color: badge.color, border: `1px solid ${badge.border}` }}>
              {badge.label}
            </span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#555', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ padding: '18px 20px', flex: 1 }}>

          {/* Contact Info */}
          <div style={S.section}>
            <div style={S.sectionHd}>Contact Info</div>
            {lead.email && (
              <div style={S.row}>
                <span style={S.rowIcon}>📧</span>
                <div><div style={S.rowLabel}>Email</div><div style={S.rowVal}>{lead.email}</div></div>
              </div>
            )}
            {lead.phone && (
              <div style={S.row}>
                <span style={S.rowIcon}>📞</span>
                <div><div style={S.rowLabel}>Phone</div><div style={S.rowVal}>{lead.phone}</div></div>
              </div>
            )}
            {(lead.city || lead.country) && (
              <div style={S.row}>
                <span style={S.rowIcon}>📍</span>
                <div><div style={S.rowLabel}>Location</div><div style={S.rowVal}>{[lead.city, lead.country].filter(Boolean).join(', ')}</div></div>
              </div>
            )}
            {lead.device_type && (
              <div style={S.row}>
                <span style={S.rowIcon}>🖥️</span>
                <div><div style={S.rowLabel}>Device</div><div style={S.rowVal}>{[lead.device_type, lead.browser].filter(Boolean).join(' · ')}</div></div>
              </div>
            )}
            {lead.traffic_source && (
              <div style={S.row}>
                <span style={S.rowIcon}>🎯</span>
                <div><div style={S.rowLabel}>Source</div><div style={{ ...S.rowVal, fontSize: 12, wordBreak: 'break-all' }}>{lead.traffic_source}</div></div>
              </div>
            )}
            <div style={S.row}>
              <span style={S.rowIcon}>🕐</span>
              <div><div style={S.rowLabel}>First seen</div><div style={S.rowVal}>{fmt(lead.first_visit_at)}</div></div>
            </div>
            <div style={{ ...S.row, marginBottom: 0 }}>
              <span style={S.rowIcon}>🕐</span>
              <div><div style={S.rowLabel}>Last seen</div><div style={S.rowVal}>{fmt(lead.last_visit_at)}</div></div>
            </div>
          </div>

          {/* Quick Actions */}
          <div style={S.section}>
            <div style={S.sectionHd}>Quick Actions</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {lead.phone && (
                <a href={'tel:' + lead.phone} style={{ ...S.actionBtn, background: 'rgba(0,213,91,0.1)', border: '1px solid rgba(0,213,91,0.3)', color: '#00d55b' }}>
                  📞 Call
                </a>
              )}
              {lead.email && (
                <a href={'mailto:' + lead.email} style={{ ...S.actionBtn, background: 'rgba(0,100,255,0.1)', border: '1px solid rgba(0,100,255,0.3)', color: '#4da6ff' }}>
                  📧 Email
                </a>
              )}
              {lead.phone && (
                <a href={whatsapp(lead.phone)} target="_blank" rel="noreferrer" style={{ ...S.actionBtn, background: 'rgba(37,211,102,0.1)', border: '1px solid rgba(37,211,102,0.3)', color: '#25d366' }}>
                  💬 WhatsApp
                </a>
              )}
            </div>
          </div>

          {/* Form Submissions */}
          <div style={S.section}>
            <div style={S.sectionHd}>Form Submissions</div>

            {loading ? (
              <div style={{ color: '#444', fontSize: 13, textAlign: 'center', padding: '16px 0' }}>Loading...</div>
            ) : submissions.length === 0 ? (
              <div style={{ color: '#333', fontSize: 13, textAlign: 'center', padding: '16px 0' }}>No form submissions yet.</div>
            ) : (
              submissions.map(sub => {
                const cFields = parseCustomFields(sub.custom_fields);
                return (
                  <div key={sub.id} style={S.subCard}>
                    <div style={S.subTime}>🕐 {fmt(sub.created_at)}</div>

                    {sub.message && (
                      <div style={S.subField}>
                        <span style={S.subLbl}>Message</span>
                        <span style={S.subVal}>{sub.message}</span>
                      </div>
                    )}
                    {sub.name && (
                      <div style={S.subField}>
                        <span style={S.subLbl}>Name</span>
                        <span style={S.subVal}>{sub.name}</span>
                      </div>
                    )}
                    {sub.email && (
                      <div style={S.subField}>
                        <span style={S.subLbl}>Email</span>
                        <span style={S.subVal}>{sub.email}</span>
                      </div>
                    )}
                    {sub.phone && (
                      <div style={S.subField}>
                        <span style={S.subLbl}>Phone</span>
                        <span style={S.subVal}>{sub.phone}</span>
                      </div>
                    )}

                    {/* Custom / industry fields */}
                    {cFields.length > 0 && (
                      <>
                        <div style={{ borderTop: '1px solid #1e1e1e', margin: '10px 0' }} />
                        {cFields.map((f, i) => (
                          <div key={f.id || i} style={S.subField}>
                            <span style={S.subLbl}>{f.label}</span>
                            <span style={S.subVal}>
                              {f.value
                                ? (f.type === 'date' ? fmtDate(f.value) : String(f.value))
                                : <span style={{ color: '#333' }}>—</span>
                              }
                            </span>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                );
              })
            )}
          </div>

        </div>
      </div>
    </>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function Leads() {
  const [leads,        setLeads]        = useState([]);
  const [search,       setSearch]       = useState('');
  const [loading,      setLoading]      = useState(true);
  const [filter,       setFilter]       = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);

  useEffect(() => {
    axios.get('/api/business/leads')
      .then(r => setLeads(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Close panel on Escape key
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setSelectedLead(null); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const filtered = leads.filter(l =>
    l.name?.toLowerCase().includes(search.toLowerCase()) ||
    l.email?.toLowerCase().includes(search.toLowerCase()) ||
    l.phone?.includes(search)
  );

  const filteredBySource = filtered.filter(l => {
    if (filter === 'all')    return true;
    if (filter === 'meta')   return l.lead_source === 'meta_ad';
    if (filter === 'google') return l.lead_source === 'google_ad';
    if (filter === 'form')   return l.lead_source === 'form';
    if (filter === 'chat')   return !l.lead_source || l.lead_source === 'chat';
    return true;
  });

  return (
    <DashLayout navItems={nav} role="Business Admin">
      <div style={{ padding: 28 }}>

        {/* Page header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700 }}>Leads</h1>
            <p style={{ color: '#555', fontSize: 13, marginTop: 4 }}>Visitors who started a chat</p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search name, email, phone..."
              style={{ padding: '9px 14px', background: '#111', border: '1px solid #1e1e1e', borderRadius: 8, color: '#fff', fontSize: 13, width: 240, outline: 'none' }}
            />
            <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 8, padding: '8px 14px', fontSize: 13, color: '#555' }}>
              Total: <span style={{ color: '#00d55b', fontWeight: 700 }}>{leads.length}</span>
            </div>
          </div>
        </div>

        {/* Source Filter */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {[['all','All'],['chat','💬 Chat'],['form','📝 Form'],['meta','📘 Meta Ad'],['google','🟢 Google Ad']].map(([key, label]) => (
            <button key={key} onClick={() => setFilter(key)}
              style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${filter === key ? '#00d55b' : '#1e1e1e'}`, background: filter === key ? 'rgba(0,213,91,0.1)' : '#111', color: filter === key ? '#00d55b' : '#555', fontSize: 12, fontWeight: filter === key ? 700 : 400, cursor: 'pointer' }}>
              {label}
            </button>
          ))}
        </div>

        {/* Lead list */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#333' }}>Loading leads...</div>
        ) : filteredBySource.length === 0 ? (
          <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, padding: 60, textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>👥</div>
            <div style={{ color: '#555', fontSize: 14 }}>No leads yet.</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            {filteredBySource.map(lead => {
              const badge = getSourceBadge(lead);
              const isSelected = selectedLead?.id === lead.id;
              return (
                <div
                  key={lead.id}
                  onClick={() => setSelectedLead(isSelected ? null : lead)}
                  style={{
                    background: isSelected ? 'rgba(0,213,91,0.04)' : '#111',
                    border: `1px solid ${isSelected ? 'rgba(0,213,91,0.3)' : '#1e1e1e'}`,
                    borderRadius: 12,
                    padding: '16px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    cursor: 'pointer',
                    transition: 'border-color 0.15s, background 0.15s',
                  }}
                >
                  {/* Avatar */}
                  <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(0,213,91,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, color: '#00d55b', flexShrink: 0 }}>
                    {lead.name?.[0]?.toUpperCase() || '?'}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <div style={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>{lead.name || 'Unknown'}</div>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: badge.bg, color: badge.color, border: `1px solid ${badge.border}` }}>
                        {badge.label}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                      {lead.email  && <span style={{ fontSize: 12, color: '#888' }}>📧 {lead.email}</span>}
                      {lead.phone  && <span style={{ fontSize: 12, color: '#888' }}>📞 {lead.phone}</span>}
                      {(lead.city || lead.country) && <span style={{ fontSize: 12, color: '#555' }}>📍 {[lead.city, lead.country].filter(Boolean).join(', ')}</span>}
                      {lead.traffic_source && lead.traffic_source !== 'direct' && (
                        <span style={{ fontSize: 12, color: '#555' }}>🎯 {lead.traffic_source}</span>
                      )}
                    </div>
                  </div>

                  {/* Chat count */}
                  <div style={{ textAlign: 'center', padding: '0 16px', borderLeft: '1px solid #1e1e1e', borderRight: '1px solid #1e1e1e' }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: '#00d55b' }}>{lead.chat_count || 0}</div>
                    <div style={{ fontSize: 11, color: '#555' }}>chats</div>
                  </div>

                  {/* Last seen */}
                  <div style={{ textAlign: 'center', padding: '0 16px', borderRight: '1px solid #1e1e1e', minWidth: 100 }}>
                    <div style={{ fontSize: 12, color: '#555' }}>Last seen</div>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{fmtDate(lead.last_visit_at)}</div>
                  </div>

                  {/* Action buttons */}
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                    {lead.phone && (
                      <a href={'tel:' + lead.phone} title="Call"
                        style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(0,213,91,0.1)', border: '1px solid rgba(0,213,91,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, textDecoration: 'none' }}>
                        📞
                      </a>
                    )}
                    {lead.email && (
                      <a href={'mailto:' + lead.email} title="Email"
                        style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(0,100,255,0.1)', border: '1px solid rgba(0,100,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, textDecoration: 'none' }}>
                        📧
                      </a>
                    )}
                    {lead.phone && (
                      <a href={whatsapp(lead.phone)} target="_blank" rel="noreferrer" title="WhatsApp"
                        style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(37,211,102,0.1)', border: '1px solid rgba(37,211,102,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, textDecoration: 'none' }}>
                        💬
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail panel */}
      {selectedLead && (
        <LeadPanel lead={selectedLead} onClose={() => setSelectedLead(null)} />
      )}
    </DashLayout>
  );
}